import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api,
  ApiError,
  type OperatorDictionaries,
  type StatementPosition,
  type TemplateAccount,
  type TemplateAccountDraft,
  type TemplateDetail,
  type TemplateMeta,
  type TemplateSummary,
} from '../api';
import type { Dictionary } from '../i18n';
import * as Icon from '@firmeto/design/Icons';

/**
 * Wzorce systemowe planu kont (E7-S1, E11-S7).
 *
 * Wzorzec to szablon, z którego firma kopiuje plan na rok obrotowy. Edycja wzorca nie dotyka
 * planów już założonych — dlatego operator może go poprawiać bez strachu, a licznik użyć mówi
 * mu, ile firm od niego zaczęło. Konta zapisujemy kompletem: lista ma kilkadziesiąt pozycji,
 * a różnice byłyby kruche przy zmianie numerów.
 */

function failureText(failure: unknown): string {
  return failure instanceof ApiError ? failure.message : String(failure);
}

export function Templates({ t, onOpen }: { t: Dictionary; onOpen: (templateId: string) => void }) {
  const [items, setItems] = useState<TemplateSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.templates().then(setItems).catch((failure) => setError(failureText(failure)));
  }, []);

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.templates.title}</h3>
          <p>{t.templates.lead}</p>
        </div>
        <div className="sp">
          <button type="button" className="btn" onClick={() => setCreating(true)} disabled={creating}>
            <Icon.Plus />
            {t.templates.new}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      {creating && items && (
        <NewTemplate t={t} sources={items} onCancel={() => setCreating(false)} onCreated={(id) => onOpen(id)} />
      )}

      {items === null && <p className="muted">{t.loading}</p>}
      {items && items.length === 0 && (
        <div className="panel">
          <p>{t.templates.none}</p>
        </div>
      )}
      {items && items.length > 0 && (
        <div className="panel">
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th className="wrap">{t.templates.name}</th>
                  <th className="wrap">{t.templates.statementTemplate}</th>
                  <th className="wrap">{t.templates.costVariant}</th>
                  <th className="n">{t.templates.accounts}</th>
                  <th className="n">{t.templates.usages}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((template) => (
                  <tr key={template.id}>
                    <td className="wrap">
                      <button type="button" className="btn ghost sm" onClick={() => onOpen(template.id)}>
                        <b>{template.name}</b>
                      </button>
                      {!template.active && <span className="chip st-warn">{t.templates.inactive}</span>}
                      {template.description && <div className="hint">{template.description}</div>}
                    </td>
                    <td className="wrap">{t.dict.statementTemplates[template.statementTemplate]}</td>
                    <td className="wrap">{t.dict.costVariants[template.costVariant]}</td>
                    <td className="n mono">{template.accountCount}</td>
                    <td className="n mono">{template.usageCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

/** Nowy wzorzec: pusty albo kopia — kopia jest punktem wyjścia, nie powiązaniem. */
function NewTemplate({
  t,
  sources,
  onCancel,
  onCreated,
}: {
  t: Dictionary;
  sources: TemplateSummary[];
  onCancel: () => void;
  onCreated: (templateId: string) => void;
}) {
  const [form, setForm] = useState({ name: '', description: '', statementTemplate: 'ZAL_5', costVariant: 'BY_KIND', copyFrom: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Kopia dziedziczy załącznik i wariant źródła — pozycje sprawozdania należą do załącznika
  function chooseSource(id: string) {
    const source = sources.find((item) => item.id === id);
    setForm({
      ...form,
      copyFrom: id,
      statementTemplate: source?.statementTemplate ?? form.statementTemplate,
      costVariant: source?.costVariant ?? form.costVariant,
    });
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await api.createTemplate(
        {
          name: form.name.trim(),
          description: form.description.trim() || null,
          statementTemplate: form.statementTemplate as TemplateMeta['statementTemplate'],
          costVariant: form.costVariant as TemplateMeta['costVariant'],
        },
        form.copyFrom || null,
      );
      onCreated(created.template.id);
    } catch (failure) {
      setError(failureText(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel" onSubmit={(event) => void submit(event)}>
      <div className="hd">
        <div>
          <h4>{t.templates.new}</h4>
          <p>{t.templates.newLead}</p>
        </div>
      </div>
      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}
      <div className="row">
        <label className="fld" style={{ flex: 2, minWidth: 260 }}>
          <span>{t.templates.name}</span>
          <input className="inp" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required autoFocus />
        </label>
        <label className="fld" style={{ flex: 2, minWidth: 260 }}>
          <span>{t.templates.copyFrom}</span>
          <select className="inp" value={form.copyFrom} onChange={(event) => chooseSource(event.target.value)}>
            <option value="">{t.templates.empty}</option>
            {sources.map((source) => (
              <option key={source.id} value={source.id}>
                {source.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="row">
        <label className="fld" style={{ flex: 1, minWidth: 220 }}>
          <span>{t.templates.statementTemplate}</span>
          <select className="inp" value={form.statementTemplate} onChange={(event) => setForm({ ...form, statementTemplate: event.target.value })}>
            {Object.keys(t.dict.statementTemplates).map((code) => (
              <option key={code} value={code}>
                {t.dict.statementTemplates[code]}
              </option>
            ))}
          </select>
        </label>
        <label className="fld" style={{ flex: 1, minWidth: 220 }}>
          <span>{t.templates.costVariant}</span>
          <select className="inp" value={form.costVariant} onChange={(event) => setForm({ ...form, costVariant: event.target.value })}>
            {Object.keys(t.dict.costVariants).map((code) => (
              <option key={code} value={code}>
                {t.dict.costVariants[code]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="fld">
        <span>{t.templates.description}</span>
        <input className="inp" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
      </label>
      <div className="row">
        <button type="submit" className="btn" disabled={busy || form.name.trim() === ''}>
          {t.templates.create}
        </button>
        <button type="button" className="btn ghost" onClick={onCancel} disabled={busy}>
          {t.templates.cancel}
        </button>
      </div>
    </form>
  );
}

// MARK: - Szczegóły i edycja

const emptyAccount: TemplateAccountDraft = {
  number: '',
  name: '',
  kind: 'BALANCE',
  side: 'DEBIT',
  settlement: false,
  currencyAccount: false,
  taxTreatment: null,
  systemRole: null,
  statementPositionDebit: null,
  statementPositionCredit: null,
};

/** Rola z bazy jest małymi literami, do API idzie wielkimi — jak w enumie. */
function toDraft(account: TemplateAccount): TemplateAccountDraft {
  return {
    number: account.number,
    name: account.name,
    kind: account.kind,
    side: account.side,
    settlement: account.settlement,
    currencyAccount: account.currencyAccount,
    taxTreatment: account.taxTreatment,
    systemRole: account.systemRole ? account.systemRole.toUpperCase() : null,
    statementPositionDebit: account.statementPositionDebit,
    statementPositionCredit: account.statementPositionCredit,
  };
}

function byNumber(a: { number: string }, b: { number: string }): number {
  return a.number.localeCompare(b.number, 'pl', { numeric: true });
}

export function TemplateEditor({ templateId, t, onBack }: { templateId: string; t: Dictionary; onBack: () => void }) {
  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const [dictionaries, setDictionaries] = useState<OperatorDictionaries | null>(null);
  const [positions, setPositions] = useState<StatementPosition[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState<TemplateMeta | null>(null);
  /** Lista w edycji — null, gdy tylko oglądamy. Zmiany są lokalne do „Zapisz komplet kont”. */
  const [drafts, setDrafts] = useState<TemplateAccountDraft[] | null>(null);
  const [editing, setEditing] = useState<{ index: number | null; draft: TemplateAccountDraft } | null>(null);

  const load = useCallback(async () => {
    try {
      const loaded = await api.template(templateId);
      setDetail(loaded);
      setMeta({
        name: loaded.template.name,
        description: loaded.template.description,
        statementTemplate: loaded.template.statementTemplate,
        costVariant: loaded.template.costVariant,
        active: loaded.template.active,
      });
      setPositions(await api.statementPositions(loaded.template.statementTemplate));
    } catch (failure) {
      setError(failureText(failure));
    }
  }, [templateId]);

  useEffect(() => {
    void load();
    api.dictionaries().then(setDictionaries).catch((failure) => setError(failureText(failure)));
  }, [load]);

  const positionLabel = useMemo(() => new Map(positions.map((position) => [position.code, position.label])), [positions]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await action();
      setSaved(true);
    } catch (failure) {
      setError(failureText(failure));
    } finally {
      setBusy(false);
    }
  }

  async function saveMeta(event: React.FormEvent) {
    event.preventDefault();
    if (!meta) return;
    await run(async () => {
      const updated = await api.updateTemplate(templateId, { ...meta, name: meta.name.trim(), description: meta.description?.trim() || null });
      setDetail(updated);
      setPositions(await api.statementPositions(updated.template.statementTemplate));
    });
  }

  async function saveAccounts() {
    if (!drafts) return;
    await run(async () => {
      setDetail(await api.replaceAccounts(templateId, drafts));
      setDrafts(null);
      setEditing(null);
    });
  }

  function applyAccount() {
    if (!editing || !drafts) return;
    const next = [...drafts];
    const draft = { ...editing.draft, number: editing.draft.number.trim(), name: editing.draft.name.trim() };
    if (editing.index === null) next.push(draft);
    else next[editing.index] = draft;
    setDrafts(next.sort(byNumber));
    setEditing(null);
  }

  if (!detail || !meta) {
    return (
      <>
        {error && (
          <div className="alert a-bad">
            <Icon.Error />
            <div>
              <b>{error}</b>
            </div>
          </div>
        )}
        <p className="muted">{t.loading}</p>
      </>
    );
  }

  const accounts: TemplateAccountDraft[] = drafts ?? detail.accounts.map(toDraft);
  const unmapped = accounts.filter((a) => a.kind !== 'OFFBALANCE' && !a.statementPositionDebit && !a.statementPositionCredit).length;
  const roles = dictionaries?.accountRoles.map((role) => role.toUpperCase()) ?? Object.keys(t.dict.roles);

  return (
    <>
      <div className="hd">
        <div>
          <h3>
            {detail.template.name}
            {!detail.template.active && <span className="chip st-warn">{t.templates.inactive}</span>}
            {drafts && <span className="chip st-info">{t.templates.unsaved}</span>}
          </h3>
          <p>
            {t.dict.statementTemplates[detail.template.statementTemplate]} · {t.dict.costVariants[detail.template.costVariant]} ·{' '}
            {t.templates.usageCount(detail.template.usageCount)}
          </p>
        </div>
        <div className="sp">
          <button type="button" className="btn ghost" onClick={onBack}>
            {t.templates.back}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}
      {saved && !error && (
        <div className="alert a-ok">
          <Icon.Check />
          <div>
            <b>{t.templates.saved}</b>
          </div>
        </div>
      )}

      <form className="panel" onSubmit={(event) => void saveMeta(event)}>
        <div className="hd">
          <div>
            <h4>{t.templates.meta}</h4>
          </div>
        </div>
        <div className="row">
          <label className="fld" style={{ flex: 2, minWidth: 260 }}>
            <span>{t.templates.name}</span>
            <input className="inp" value={meta.name} onChange={(event) => setMeta({ ...meta, name: event.target.value })} required />
          </label>
          <label className="fld" style={{ flex: 1, minWidth: 220 }}>
            <span>{t.templates.statementTemplate}</span>
            <select
              className="inp"
              value={meta.statementTemplate}
              onChange={(event) => setMeta({ ...meta, statementTemplate: event.target.value as TemplateMeta['statementTemplate'] })}
            >
              {Object.keys(t.dict.statementTemplates).map((code) => (
                <option key={code} value={code}>
                  {t.dict.statementTemplates[code]}
                </option>
              ))}
            </select>
          </label>
          <label className="fld" style={{ flex: 1, minWidth: 220 }}>
            <span>{t.templates.costVariant}</span>
            <select
              className="inp"
              value={meta.costVariant}
              onChange={(event) => setMeta({ ...meta, costVariant: event.target.value as TemplateMeta['costVariant'] })}
            >
              {Object.keys(t.dict.costVariants).map((code) => (
                <option key={code} value={code}>
                  {t.dict.costVariants[code]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="fld">
          <span>{t.templates.description}</span>
          <input className="inp" value={meta.description ?? ''} onChange={(event) => setMeta({ ...meta, description: event.target.value })} />
        </label>
        <label className="row" style={{ alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={meta.active} onChange={(event) => setMeta({ ...meta, active: event.target.checked })} />
          <span>{t.templates.activeLabel}</span>
        </label>
        <div className="row">
          <button type="submit" className="btn" disabled={busy || meta.name.trim() === ''}>
            {t.templates.save}
          </button>
        </div>
      </form>

      <div className="panel">
        <div className="hd">
          <div>
            <h4>
              {t.templates.accounts} <span className="muted mono">({accounts.length})</span>
            </h4>
            <p>{t.templates.accountsHint}</p>
          </div>
          <div className="sp">
            {drafts ? (
              <>
                <button type="button" className="btn ghost sm" onClick={() => setEditing({ index: null, draft: emptyAccount })} disabled={busy}>
                  <Icon.Plus />
                  {t.templates.addAccount}
                </button>
                <button
                  type="button"
                  className="btn ghost sm"
                  onClick={() => {
                    setDrafts(null);
                    setEditing(null);
                  }}
                  disabled={busy}
                >
                  {t.templates.discard}
                </button>
                <button type="button" className="btn sm" onClick={() => void saveAccounts()} disabled={busy}>
                  {t.templates.saveAccounts}
                </button>
              </>
            ) : (
              <button type="button" className="btn ghost sm" onClick={() => setDrafts(detail.accounts.map(toDraft))}>
                {t.templates.editAccounts}
              </button>
            )}
          </div>
        </div>

        {unmapped > 0 && (
          <div className="alert a-warn">
            <Icon.Warn />
            <div>{t.templates.unmapped(unmapped)}</div>
          </div>
        )}

        {editing && drafts && (
          <AccountForm
            t={t}
            draft={editing.draft}
            isNew={editing.index === null}
            roles={roles}
            settlementRoles={dictionaries?.settlementRoles.map((role) => role.toUpperCase()) ?? ['RECEIVABLES', 'PAYABLES']}
            positions={positions}
            onChange={(draft) => setEditing({ ...editing, draft })}
            onApply={applyAccount}
            onCancel={() => setEditing(null)}
          />
        )}

        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>{t.templates.number}</th>
                <th className="wrap">{t.templates.accountName}</th>
                <th>{t.templates.kind}</th>
                <th>{t.templates.side}</th>
                <th className="wrap">{t.templates.role}</th>
                <th className="wrap">{t.templates.position}</th>
                {drafts && <th />}
              </tr>
            </thead>
            <tbody>
              {accounts.map((account, index) => (
                <tr key={`${account.number}-${index}`}>
                  <td className="mono">{account.number}</td>
                  <td className="wrap">
                    {account.number.includes('-') ? <span style={{ paddingLeft: 12 }}>{account.name}</span> : <b>{account.name}</b>}
                    <div className="hint">
                      {[account.settlement && t.templates.settlement, account.currencyAccount && t.templates.currency, account.taxTreatment && t.dict.tax[account.taxTreatment]]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </td>
                  <td>{t.dict.kinds[account.kind]}</td>
                  <td>{t.dict.sides[account.side]}</td>
                  <td className="wrap">{account.systemRole ? <span className="chip st-info">{t.dict.roles[account.systemRole] ?? account.systemRole}</span> : ''}</td>
                  <td className="wrap">
                    {[account.statementPositionDebit, account.statementPositionCredit]
                      .filter((code): code is string => Boolean(code))
                      .map((code) => positionLabel.get(code) ?? code)
                      .join(' / ')}
                  </td>
                  {drafts && (
                    <td className="n">
                      <div className="sp">
                        <button type="button" className="btn ghost sm" onClick={() => setEditing({ index, draft: account })} disabled={busy}>
                          {t.templates.edit}
                        </button>
                        <button
                          type="button"
                          className="btn ghost sm danger"
                          onClick={() => setDrafts(drafts.filter((_, i) => i !== index))}
                          disabled={busy}
                        >
                          {t.templates.remove}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/** Formularz jednego konta — te same pola, co w planie kont firmy, plus rola dla schematów. */
function AccountForm({
  t,
  draft,
  isNew,
  roles,
  settlementRoles,
  positions,
  onChange,
  onApply,
  onCancel,
}: {
  t: Dictionary;
  draft: TemplateAccountDraft;
  isNew: boolean;
  roles: string[];
  settlementRoles: string[];
  positions: StatementPosition[];
  onChange: (draft: TemplateAccountDraft) => void;
  onApply: () => void;
  onCancel: () => void;
}) {
  const offBalance = draft.kind === 'OFFBALANCE';
  const assignable = positions.filter((position) => position.assignable);
  const sections = ['ASSETS', 'LIABILITIES', 'INCOME'] as const;

  const positionSelect = (field: 'statementPositionDebit' | 'statementPositionCredit', label: string) => (
    <label className="fld" style={{ flex: 1, minWidth: 260 }}>
      <span>{label}</span>
      <select className="inp" value={draft[field] ?? ''} disabled={offBalance} onChange={(event) => onChange({ ...draft, [field]: event.target.value || null })}>
        <option value="">{t.templates.positionNone}</option>
        {sections.map((section) => (
          <optgroup key={section} label={t.dict.sections[section]}>
            {assignable
              .filter((position) => position.section === section)
              .map((position) => (
                <option key={position.code} value={position.code}>
                  {' '.repeat(Math.max(0, position.level - 1) * 3)}
                  {position.label}
                </option>
              ))}
          </optgroup>
        ))}
      </select>
    </label>
  );

  // Zmiana rodzaju porządkuje pola, które do niego nie pasują — reguły planu kont odrzuciłyby je
  function changeKind(kind: TemplateAccountDraft['kind']) {
    onChange({
      ...draft,
      kind,
      side: kind === 'OFFBALANCE' ? 'NONE' : draft.side === 'NONE' ? 'DEBIT' : draft.side,
      settlement: kind === 'BALANCE' ? draft.settlement : false,
      taxTreatment: kind === 'RESULT' ? draft.taxTreatment : null,
      statementPositionDebit: kind === 'OFFBALANCE' ? null : draft.statementPositionDebit,
      statementPositionCredit: kind === 'OFFBALANCE' ? null : draft.statementPositionCredit,
    });
  }

  return (
    <div className="panel" style={{ marginBottom: 12 }}>
      <div className="hd">
        <div>
          <h4>{isNew ? t.templates.addAccount : t.templates.edit}</h4>
          <p>{t.templates.numberHint}</p>
        </div>
      </div>
      <div className="row">
        <label className="fld">
          <span>{t.templates.number}</span>
          <input className="inp mono" style={{ maxWidth: 140 }} value={draft.number} onChange={(event) => onChange({ ...draft, number: event.target.value })} autoFocus={isNew} />
        </label>
        <label className="fld" style={{ flex: 2, minWidth: 260 }}>
          <span>{t.templates.accountName}</span>
          <input className="inp" value={draft.name} onChange={(event) => onChange({ ...draft, name: event.target.value })} />
        </label>
      </div>
      <div className="row">
        <label className="fld">
          <span>{t.templates.kind}</span>
          <select className="inp" value={draft.kind} onChange={(event) => changeKind(event.target.value as TemplateAccountDraft['kind'])}>
            {Object.keys(t.dict.kinds).map((kind) => (
              <option key={kind} value={kind}>
                {t.dict.kinds[kind]}
              </option>
            ))}
          </select>
        </label>
        <label className="fld">
          <span>{t.templates.side}</span>
          <select className="inp" value={draft.side} disabled={offBalance} onChange={(event) => onChange({ ...draft, side: event.target.value as TemplateAccountDraft['side'] })}>
            {(offBalance ? ['NONE'] : ['DEBIT', 'CREDIT', 'BOTH']).map((side) => (
              <option key={side} value={side}>
                {t.dict.sides[side]}
              </option>
            ))}
          </select>
        </label>
        <label className="fld">
          <span>{t.templates.tax}</span>
          <select
            className="inp"
            value={draft.taxTreatment ?? ''}
            disabled={draft.kind !== 'RESULT'}
            onChange={(event) => onChange({ ...draft, taxTreatment: (event.target.value || null) as TemplateAccountDraft['taxTreatment'] })}
          >
            <option value="">{t.templates.taxNone}</option>
            {Object.keys(t.dict.tax).map((code) => (
              <option key={code} value={code}>
                {t.dict.tax[code]}
              </option>
            ))}
          </select>
        </label>
        <label className="fld" style={{ flex: 1, minWidth: 240 }}>
          <span>{t.templates.role}</span>
          <select
            className="inp"
            value={draft.systemRole ?? ''}
            onChange={(event) => {
              const role = event.target.value || null;
              // Rola rozrachunkowa zakłada konto rozrachunkowe — inaczej dekret ze schematu nie przejdzie
              onChange({ ...draft, systemRole: role, settlement: role && settlementRoles.includes(role) ? true : draft.settlement });
            }}
          >
            <option value="">{t.templates.roleNone}</option>
            {roles.map((role) => (
              <option key={role} value={role}>
                {t.dict.roles[role] ?? role}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="row">
        <label className="row" style={{ alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={draft.settlement} disabled={draft.kind !== 'BALANCE'} onChange={(event) => onChange({ ...draft, settlement: event.target.checked })} />
          <span>{t.templates.settlement}</span>
        </label>
        <label className="row" style={{ alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={draft.currencyAccount} onChange={(event) => onChange({ ...draft, currencyAccount: event.target.checked })} />
          <span>{t.templates.currency}</span>
        </label>
      </div>
      {!offBalance && (
        <div className="row">
          {positionSelect('statementPositionDebit', t.templates.positionDebit)}
          {positionSelect('statementPositionCredit', t.templates.positionCredit)}
        </div>
      )}
      <div className="row">
        <button type="button" className="btn" onClick={onApply} disabled={draft.number.trim() === '' || draft.name.trim() === ''}>
          {t.templates.apply}
        </button>
        <button type="button" className="btn ghost" onClick={onCancel}>
          {t.templates.cancel}
        </button>
      </div>
    </div>
  );
}
